import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Layout } from '../components/Layout';
import { Badge } from '../components/Badge';
import { Button } from '../components/Button';
import { Mail, ChevronDown, ChevronUp, Users, MousePointerClick, Reply, Plus, X, Edit2, Trash2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useRole } from '../contexts/RoleContext';
// Only email campaigns are serviced here.
type Channel = 'email';
type CampaignStatus = 'active' | 'paused';
interface CampaignStep {
  day: number;
  subject: string;
  body: string;
  channel: 'email';
}
interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  body: string;
}
// Reusable email templates admins can drop into any campaign step.
const EMAIL_TEMPLATES: EmailTemplate[] = [{
  id: 'welcome',
  name: 'Welcome / Intro',
  subject: "Welcome to homebuying! Let's get started.",
  body: "Hi there,\n\nWelcome to the homebuying process! We're excited to help you find your dream home. Let's schedule a quick call to discuss your goals.\n\nBest,\nYour Mortgage Team"
}, {
  id: 'preapproval',
  name: 'Pre-approval Education',
  subject: 'Understanding pre-approval vs pre-qualification',
  body: "Understanding the difference between pre-approval and pre-qualification can save you a lot of time. Pre-qualification is a quick estimate, while pre-approval is a verified commitment that makes your offer stronger.\n\nReply to this email and we'll get you pre-approved."
}, {
  id: 'rate-drop',
  name: 'Rate Drop Alert',
  subject: 'Rates just dropped — here is what it could mean for your loan',
  body: "Great news! Interest rates have just dropped. This could significantly lower your monthly payments. Let's chat about your options and see how much you could save."
}, {
  id: 'checklist',
  name: 'Document Checklist',
  subject: 'Ready to look at houses? Here is a checklist.',
  body: 'Before you start touring homes, make sure you have these ready:\n\n• Recent pay stubs\n• Two years of tax returns\n• Bank statements\n• Photo ID\n\nHave questions on any of these? Just reply.'
}, {
  id: 'check-in',
  name: 'Friendly Check-in',
  subject: 'Checking in — how can we help?',
  body: "Hi,\n\nJust checking in to see how things are going and whether you have any questions about your loan or the homebuying process. We're here whenever you're ready."
}, {
  id: 'anniversary',
  name: 'Post-Closing Anniversary',
  subject: "Happy homeversary! Let's review your equity.",
  body: "Congratulations on your home! This is a great time to review your home's equity and see if refinancing could lower your rate or shorten your term. Want a quick review?"
}];
interface Campaign {
  id: number;
  name: string;
  type: Channel;
  enrolled: number;
  openRate: string;
  clickRate: string;
  replyRate: string;
  status: CampaignStatus;
  steps: CampaignStep[];
}
const INITIAL_CAMPAIGNS: Campaign[] = [{
  id: 1,
  name: 'First-Time Buyer Nurture',
  type: 'email',
  enrolled: 124,
  openRate: '45%',
  clickRate: '12%',
  replyRate: '4%',
  status: 'active',
  steps: [{
    day: 1,
    subject: "Welcome to homebuying! Let's get started.",
    body: "Hi there,\n\nWelcome to the homebuying process! We're excited to help you find your dream home. Let's schedule a quick call to discuss your goals.\n\nBest,\nYour Mortgage Team",
    channel: 'email'
  }, {
    day: 3,
    subject: 'Understanding pre-approval vs pre-qualification',
    body: "Understanding the difference between pre-approval and pre-qualification can save you a lot of time. Let's break it down...",
    channel: 'email'
  }, {
    day: 7,
    subject: 'Top 5 mistakes first-time buyers make',
    body: 'Avoid these common pitfalls when buying your first home to ensure a smooth process.',
    channel: 'email'
  }, {
    day: 14,
    subject: 'Ready to look at houses? Here is a checklist.',
    body: 'Before you start touring homes, make sure you have everything on this checklist ready to go.',
    channel: 'email'
  }]
}, {
  id: 2,
  name: 'Rate Drop Alert',
  type: 'email',
  enrolled: 89,
  openRate: '52%',
  clickRate: '18%',
  replyRate: '6%',
  status: 'active',
  steps: [{
    day: 1,
    subject: 'Rates just dropped — here is what it could mean for your loan',
    body: "Great news! Interest rates have just dropped. This could significantly lower your monthly payments. Let's chat about your options.",
    channel: 'email'
  }]
}, {
  id: 3,
  name: 'Post-Closing Follow-up',
  type: 'email',
  enrolled: 45,
  openRate: '68%',
  clickRate: '15%',
  replyRate: '8%',
  status: 'paused',
  steps: [{
    day: 30,
    subject: 'Happy 1-month anniversary in your new home!',
    body: "Can you believe it's already been a month? We hope you are settling in well.",
    channel: 'email'
  }, {
    day: 180,
    subject: 'Checking in - how is the house?',
    body: "It's been 6 months since you moved in! How are things going with the new place?",
    channel: 'email'
  }, {
    day: 365,
    subject: "Happy 1-year homeversary! Let's review your equity.",
    body: "Happy 1-year anniversary in your home! This is a great time to review your home's equity and see if refinancing makes sense.",
    channel: 'email'
  }]
}];
// Shared sequence-step editor used by both the Create and Edit campaign modals.
function StepSequenceEditor({
  steps,
  setSteps
}: {
  steps: CampaignStep[];
  setSteps: React.Dispatch<React.SetStateAction<CampaignStep[]>>;
}) {
  const addStep = () => {
    const maxDay = steps.length > 0 ? Math.max(...steps.map(s => s.day)) : 0;
    setSteps(prev => [...prev, {
      day: maxDay + 1,
      subject: '',
      body: '',
      channel: 'email'
    }]);
  };
  const updateStep = (index: number, field: keyof CampaignStep, value: any) => {
    setSteps(prev => prev.map((s, i) => i === index ? {
      ...s,
      [field]: value
    } : s));
  };
  const removeStep = (index: number) => {
    setSteps(prev => prev.filter((_, i) => i !== index));
  };
  const applyTemplate = (index: number, templateId: string) => {
    const template = EMAIL_TEMPLATES.find(t => t.id === templateId);
    if (!template) return;
    setSteps(prev => prev.map((s, i) => i === index ? {
      ...s,
      subject: template.subject,
      body: template.body
    } : s));
  };
  return <div data-id="element-291" data-mp="14se2kc8khg5b">
      <div className="flex items-center justify-between mb-4" data-id="element-292" data-mp="14s5o4fwsq2s0">
        <h3 className="text-sm font-medium text-slate-900" data-id="element-293" data-mp="1e4v016odgvnt">Sequence Steps</h3>
        <Button type="button" variant="outline" size="sm" icon={<Plus size={14} data-id="element-295" data-mp="1nv6hlw0zb3d6" />} onClick={addStep} data-id="element-294" data-mp="1a4b1pzs2bm03">
          Add Step
        </Button>
      </div>

      <div className="space-y-4" data-id="element-296" data-mp="1ivuzaikyrpth">
        {steps.map((step, index) => <div key={index} className="p-4 border border-slate-200 rounded-lg bg-slate-50 space-y-3 relative group" data-id="element-297" data-mp="v6si1bu8ryzi" data-mpi={index}>
            <button type="button" onClick={() => removeStep(index)} className="absolute top-3 right-3 p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors opacity-0 group-hover:opacity-100" title="Remove step" data-id="element-298" data-mp="r6iahuqsx0ij">
              <Trash2 size={16} data-id="element-299" data-mp="2giae1cm0a9dc" />
            </button>

            <div className="flex flex-wrap items-end gap-4 pr-8" data-id="element-300" data-mp="2bljzwutrhnuk">
              <div className="w-24" data-id="element-301" data-mp="2bnek26x7lbxl">
                <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-302" data-mp="9wnvhu47ex12">
                  Send on Day
                </label>
                <input type="number" min="1" value={step.day} onChange={e => updateStep(index, 'day', parseInt(e.target.value) || 1)} className="w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-303" data-mp="dxpwogholj5o" />
              </div>

              <div className="flex-1 min-w-[12rem]" data-id="element-304" data-mp="11najjagyh0k8">
                <label htmlFor={`template-${index}`} className="block text-xs font-medium text-slate-500 mb-1" data-id="element-305" data-mp="11ntycibb78u1">
                  Email Template
                </label>
                <select id={`template-${index}`} value="" onChange={e => {
              if (e.target.value) applyTemplate(index, e.target.value);
            }} className="w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-306" data-mp="33grykz7f13p0">
                  <option value="" data-id="element-307" data-mp="2m5cuvpl6qbhl">Choose a template…</option>
                  {EMAIL_TEMPLATES.map(t => <option key={t.id} value={t.id} data-id="element-308" data-mp="3jt3j902csx0h">
                      {t.name}
                    </option>)}
                </select>
              </div>
            </div>

            <div data-id="element-309" data-mp="39u4hm1g0a1mz">
              <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-310" data-mp="39tmh2smb9199">
                Email Subject
              </label>
              <input type="text" value={step.subject} onChange={e => updateStep(index, 'subject', e.target.value)} placeholder="Subject line" className="w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-311" data-mp="35ssnbim6ss8g" />
            </div>

            <div data-id="element-312" data-mp="2yj8w5g7om055">
              <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-313" data-mp="3ccqdcucwb0s0">
                Email Body
              </label>
              <textarea value={step.body} onChange={e => updateStep(index, 'body', e.target.value)} rows={3} placeholder="Email template body..." className="w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y" data-id="element-314" data-mp="37lrv3p8cjsm4" />
            </div>
          </div>)}
        {steps.length === 0 && <div className="text-center py-6 text-sm text-slate-500 border border-dashed border-slate-300 rounded-lg" data-id="element-315" data-mp="20xl6rf9n8i34">
            No steps in this sequence. Add a step to get started.
          </div>}
      </div>
    </div>;
}
interface CreateCampaignModalProps {
  open: boolean;
  onClose: () => void;
  onCreate: (campaign: Campaign) => void;
}
function CreateCampaignModal({
  open,
  onClose,
  onCreate
}: CreateCampaignModalProps) {
  const [name, setName] = useState('');
  const [status, setStatus] = useState<CampaignStatus>('active');
  const [steps, setSteps] = useState<CampaignStep[]>([]);
  useEffect(() => {
    if (open) {
      setName('');
      setStatus('active');
      setSteps([]);
    }
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onCreate({
      id: Date.now(),
      name: name.trim(),
      type: 'email',
      enrolled: 0,
      openRate: '0%',
      clickRate: '0%',
      replyRate: '0%',
      status,
      steps: [...steps].sort((a, b) => a.day - b.day)
    });
    onClose();
  };
  return createPortal(<AnimatePresence data-id="element-316" data-mp="2m4un12zcrfu0">
      {open && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-317" data-mp="2i0mxj3l2lvuq">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} aria-hidden="true" data-id="element-318" data-mp="2d21yh7hk1i7v" />
          <motion.div role="dialog" aria-modal="true" aria-labelledby="create-campaign-title" initial={{
        opacity: 0,
        scale: 0.96,
        y: 8
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 8
      }} transition={{
        duration: 0.15
      }} className="relative w-full max-w-2xl max-h-[90vh] flex flex-col bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden" data-id="element-319" data-mp="30yfwxvwhjnnd">
            <div className="flex items-start justify-between p-6 border-b border-slate-200 shrink-0" data-id="element-320" data-mp="dvwipnexl3nt">
              <div className="flex items-center gap-3" data-id="element-321" data-mp="itompfsuog8d">
                <div className="p-2.5 rounded-lg bg-blue-50 text-blue-600" data-id="element-322" data-mp="nt159ocov155">
                  <Plus size={20} data-id="element-323" data-mp="nqy90mky0brz" />
                </div>
                <div data-id="element-324" data-mp="slc5ksbccoy8">
                  <h2 id="create-campaign-title" className="text-lg font-semibold text-slate-900" data-id="element-325" data-mp="sksxe3bhtexe">
                    Create Campaign
                  </h2>
                  <p className="text-sm text-slate-500" data-id="element-326" data-mp="11iuwuefkvzik">
                    Set up a new drip sequence and its steps.
                  </p>
                </div>
              </div>
              <button type="button" onClick={onClose} aria-label="Close" className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-colors" data-id="element-327" data-mp="sv8qa7x0tpxv">
                <X size={20} data-id="element-328" data-mp="xnifflqbalz3" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden" data-id="element-329" data-mp="9y7iygfzvdqr">
              <div className="p-6 space-y-6 overflow-y-auto" data-id="element-330" data-mp="5xnqvraa30bh">
                <div className="grid grid-cols-2 gap-4" data-id="element-331" data-mp="jwdn74p9wvys">
                  <div data-id="element-332" data-mp="jxygys3rkavl">
                    <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-333" data-mp="ju0vw0xml992">
                      Campaign name
                    </label>
                    <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Refinance Re-engagement" autoFocus className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-334" data-mp="1vahge8o1eg2s" />
                  </div>
                  <div data-id="element-335" data-mp="3487gw0p1vl1b">
                    <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-336" data-mp="347pfkpnj6gmj">
                      Status
                    </label>
                    <select value={status} onChange={e => setStatus(e.target.value as CampaignStatus)} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-337" data-mp="3s065tnhf9ewx">
                      <option value="active" data-id="element-338" data-mp="eq8egeapfb9i">Active</option>
                      <option value="paused" data-id="element-339" data-mp="jjbe9462rgy5">Paused</option>
                    </select>
                  </div>
                </div>
                <p className="text-xs text-slate-400" data-id="element-340" data-mp="tdgy83i2wox3">
                  All campaigns are delivered over email.
                </p>

                <StepSequenceEditor steps={steps} setSteps={setSteps} data-id="element-341" data-mp="jg2ladvd6i9j" />
              </div>

              <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-200 bg-slate-50 shrink-0" data-id="element-342" data-mp="egzofgpjficn">
                <Button type="button" variant="outline" onClick={onClose} data-id="element-343" data-mp="afw9cwfbdqch">
                  Cancel
                </Button>
                <Button type="submit" variant="primary" icon={<Plus size={16} data-id="element-345" data-mp="qmdd5hcnnj3" />} disabled={!name.trim()} data-id="element-344" data-mp="5nmjthi42saq">
                  Create Campaign
                </Button>
              </div>
            </form>
          </motion.div>
        </div>}
    </AnimatePresence>, document.body);
}
interface EditCampaignModalProps {
  campaign: Campaign | null;
  open: boolean;
  onClose: () => void;
  onSave: (campaign: Campaign) => void;
}
function EditCampaignModal({
  campaign,
  open,
  onClose,
  onSave
}: EditCampaignModalProps) {
  const [name, setName] = useState('');
  const [status, setStatus] = useState<CampaignStatus>('active');
  const [steps, setSteps] = useState<CampaignStep[]>([]);
  useEffect(() => {
    if (campaign && open) {
      setName(campaign.name);
      setStatus(campaign.status);
      setSteps(campaign.steps.map(s => ({
        ...s
      })));
    }
  }, [campaign, open]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!campaign || !name.trim()) return;
    onSave({
      ...campaign,
      name: name.trim(),
      status,
      steps: [...steps].sort((a, b) => a.day - b.day)
    });
    onClose();
  };
  return createPortal(<AnimatePresence data-id="element-346" data-mp="3lgqczmcx2u0r">
      {open && campaign && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-347" data-mp="3lib5z7jl24wi">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} aria-hidden="true" data-id="element-348" data-mp="3c6mp9hb0qa5" />
          <motion.div role="dialog" aria-modal="true" aria-labelledby="edit-campaign-title" initial={{
        opacity: 0,
        scale: 0.96,
        y: 8
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 8
      }} transition={{
        duration: 0.15
      }} className="relative w-full max-w-2xl max-h-[90vh] flex flex-col bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden" data-id="element-349" data-mp="m1lh4l2hih5k">
            <div className="flex items-start justify-between p-6 border-b border-slate-200 shrink-0" data-id="element-350" data-mp="10qo8yo017sft">
              <div className="flex items-center gap-3" data-id="element-351" data-mp="15p7uwoeml486">
                <div className="p-2.5 rounded-lg bg-blue-50 text-blue-600" data-id="element-352" data-mp="rv8blx02iv5h">
                  <Edit2 size={20} data-id="element-353" data-mp="wmymq56txmxy" />
                </div>
                <div data-id="element-354" data-mp="wojke3ofxm01">
                  <h2 id="edit-campaign-title" className="text-lg font-semibold text-slate-900" data-id="element-355" data-mp="hy2jclfpwy3o">
                    Edit Campaign
                  </h2>
                  <p className="text-sm text-slate-500" data-id="element-356" data-mp="8wvyk061d14m">
                    Update sequence steps and templates.
                  </p>
                </div>
              </div>
              <button type="button" onClick={onClose} aria-label="Close" className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-colors" data-id="element-357" data-mp="2zb4csfd9eu26">
                <X size={20} data-id="element-358" data-mp="2qc0rr5uj84l1" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden" data-id="element-359" data-mp="2ub9liapko2gl">
              <div className="p-6 space-y-6 overflow-y-auto" data-id="element-360" data-mp="3i2dvnf8x6v6f">
                <div className="grid grid-cols-2 gap-4" data-id="element-361" data-mp="3n0ym4myde0ho">
                  <div data-id="element-362" data-mp="3n2jf4851dbdf">
                    <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-363" data-mp="399ttw5xc0ahd">
                      Campaign name
                    </label>
                    <input type="text" value={name} onChange={e => setName(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-364" data-mp="3d92o1bwaaadg" />
                  </div>
                  <div data-id="element-365" data-mp="1pc3907h0ffcn">
                    <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-366" data-mp="1pcuqyfhopekg">
                      Status
                    </label>
                    <select value={status} onChange={e => setStatus(e.target.value as CampaignStatus)} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-367" data-mp="1z37nl62rwy6y">
                      <option value="active" data-id="element-368" data-mp="1gcpv92lus4m4">Active</option>
                      <option value="paused" data-id="element-369" data-mp="1gb4zxarpshn7">Paused</option>
                    </select>
                  </div>
                </div>

                <StepSequenceEditor steps={steps} setSteps={setSteps} data-id="element-370" data-mp="2rrlk1he7rmge" />
              </div>

              <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-200 bg-slate-50 shrink-0" data-id="element-371" data-mp="2rty2rwrsq0ui">
                <Button type="button" variant="outline" onClick={onClose} data-id="element-372" data-mp="33td50c83kygd">
                  Cancel
                </Button>
                <Button type="submit" variant="primary" disabled={!name.trim()} data-id="element-373" data-mp="38u0uuuklb990">
                  Save Changes
                </Button>
              </div>
            </form>
          </motion.div>
        </div>}
    </AnimatePresence>, document.body);
}
export function Campaigns() {
  const {
    role
  } = useRole();
  const [campaigns, setCampaigns] = useState<Campaign[]>(INITIAL_CAMPAIGNS);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const handleCreate = (campaign: Campaign) => {
    setCampaigns(prev => [campaign, ...prev]);
    setExpanded(campaign.id);
  };
  const handleUpdate = (updated: Campaign) => {
    setCampaigns(prev => prev.map(c => c.id === updated.id ? updated : c));
  };
  return <Layout data-id="element-374" data-mp="fggyfwlhdyte">
      <div className="max-w-7xl mx-auto space-y-6" data-id="element-375" data-mp="ffxp349wciqz">
        <div className="flex justify-between items-end" data-id="element-376" data-mp="fe311h3bwaio">
          <div data-id="element-377" data-mp="ag1m98cxqxd0">
            <h1 className="text-2xl font-bold text-slate-900" data-id="element-378" data-mp="afjepfktowps">Campaigns</h1>
            <p className="text-slate-500 mt-1" data-id="element-379" data-mp="5gyehg5kmd1c">
              Manage your automated email sequences.
            </p>
          </div>
          {role === 'admin' && <Button variant="primary" icon={<Plus size={16} data-id="element-381" data-mp="3rw18gmwq44io" />} onClick={() => setCreateOpen(true)} data-id="element-380" data-mp="3rxm3edmy9ph2">
              Create Campaign
            </Button>}
        </div>

        <div className="grid grid-cols-1 gap-4" data-id="element-382" data-mp="3ixz3xxds6tqn">
          {campaigns.map(c => <div key={c.id} className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden transition-all hover:border-slate-300" data-id="element-383" data-mp="3dyctfvpc7rlj">
              <div className="p-6 flex flex-col md:flex-row md:items-center justify-between cursor-pointer gap-4" onClick={() => setExpanded(expanded === c.id ? null : c.id)} data-id="element-384" data-mp="1cv23qh1gcqkg">
                <div className="flex items-center gap-4" data-id="element-385" data-mp="134r1r01bwrg7">
                  <div className="p-3 rounded-xl bg-blue-50 text-blue-600" data-id="element-386" data-mp="184vgakiofc3s">
                    <Mail size={24} data-id="element-387" data-mp="182siheba8eoi" />
                  </div>
                  <div data-id="element-388" data-mp="u42jtu8te6y1">
                    <div className="flex items-center gap-2" data-id="element-389" data-mp="u3st24fy3vra">
                      <h3 className="text-lg font-semibold text-slate-900" data-id="element-390" data-mp="z4gtthtz7qyp">
                        {c.name}
                      </h3>
                      <Badge variant={c.status === 'active' ? 'success' : 'default'} data-id="element-391" data-mp="k8r5hd1bj1qt">
                        {c.status}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 mt-1.5 text-sm text-slate-500" data-id="element-392" data-mp="2ej4m3uwviorh">
                      <span className="flex items-center gap-1.5" data-id="element-393" data-mp="2jho79t3n7wis">
                        <Users size={14} data-id="element-394" data-mp="7xgk7yzk3ec9" /> {c.enrolled} enrolled
                      </span>
                      <span className="w-1 h-1 rounded-full bg-slate-300" data-id="element-395" data-mp="2ywy9ykyq2jw" />
                      <span data-id="element-396" data-mp="hmrsxkij6gzm">{c.steps.length} steps</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-8 md:ml-auto mr-4" data-id="element-397" data-mp="cv035c778faj">
                  <div className="text-center" data-id="element-398" data-mp="qmeuc6b9fmso">
                    <div className="text-lg font-semibold text-slate-900" data-id="element-399" data-mp="qp9erxu9x7m3">
                      {c.openRate}
                    </div>
                    <div className="text-xs text-slate-500 flex items-center gap-1" data-id="element-400" data-mp="10lvzmmmlnyo4">
                      <Mail size={12} data-id="element-401" data-mp="vm9jo5fe04bm" /> Open
                    </div>
                  </div>
                  <div className="text-center" data-id="element-402" data-mp="32cpl1qj9ra41">
                    <div className="text-lg font-semibold text-slate-900" data-id="element-403" data-mp="32dihnyoikj8g">
                      {c.clickRate}
                    </div>
                    <div className="text-xs text-slate-500 flex items-center gap-1" data-id="element-404" data-mp="2xfz5d4bdmgj5">
                      <MousePointerClick size={12} data-id="element-405" data-mp="11cc8q2jmmbp2" /> Click
                    </div>
                  </div>
                  <div className="text-center" data-id="element-406" data-mp="16bowqqm8hot8">
                    <div className="text-lg font-semibold text-slate-900" data-id="element-407" data-mp="1aawl6lylfp4u">
                      {c.replyRate}
                    </div>
                    <div className="text-xs text-slate-500 flex items-center gap-1" data-id="element-408" data-mp="1fbkbt6iwu3yj">
                      <Reply size={12} data-id="element-409" data-mp="1k8ua7boa1pyi" /> Reply
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2" data-id="element-410" data-mp="1p6lxtthw2pww">
                  {role === 'admin' && <button type="button" onClick={e => {
                e.stopPropagation();
                setEditingCampaign(c);
              }} className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors" title="Edit Campaign" data-id="element-411" data-mp="1u19ska3hkgjx">
                      <Edit2 size={18} data-id="element-412" data-mp="2i0sbliufh8wu" />
                    </button>}
                  <div className="hidden md:block p-2 text-slate-400 hover:bg-slate-100 rounded-full transition-colors" data-id="element-413" data-mp="2d2h25981p0f5">
                    {expanded === c.id ? <ChevronUp size={20} data-id="element-414" data-mp="2445b5639o0m6" /> : <ChevronDown size={20} data-id="element-415" data-mp="30t34cjvdbqh8" />}
                  </div>
                </div>
              </div>

              <AnimatePresence data-id="element-416" data-mp="2vvkvethcpb98">
                {expanded === c.id && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} className="bg-slate-50 border-t border-slate-100 overflow-hidden" data-id="element-417" data-mp="2r31fvqz3s61s">
                    <div className="p-6" data-id="element-418" data-mp="72qnz6o9a0du">
                      <h4 className="font-medium text-slate-900 mb-4" data-id="element-419" data-mp="73tf9fphq4ua">
                        Sequence Steps
                      </h4>
                      {c.steps.length > 0 ? <div className="space-y-3 relative before:absolute before:inset-0 before:ml-[1.1rem] before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-slate-200" data-id="element-420" data-mp="3til5d0yuqjad">
                          {c.steps.map((step, i) => <div key={i} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group" data-id="element-421" data-mp="b3272f0yzsn7" data-mpi={i}>
                              <div className="flex items-center justify-center w-9 h-9 rounded-full border-4 border-slate-50 bg-white text-slate-500 shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 shadow-sm z-10" data-id="element-422" data-mp="2vszyeitvwhqk">
                                <Mail size={14} data-id="element-423" data-mp="1dx7pvrapzjv9" />
                              </div>
                              <div className="w-[calc(100%-3rem)] md:w-[calc(50%-2.5rem)] p-4 rounded-lg border border-slate-200 bg-white shadow-sm" data-id="element-424" data-mp="1rw7cyuil3qpb">
                                <div className="flex items-center justify-between mb-2" data-id="element-425" data-mp="1nvdpu3cyufxh">
                                  <Badge variant="default" data-id="element-426" data-mp="1062xna9oltuy">
                                    Day {step.day}
                                  </Badge>
                                  <span className="text-xs font-medium text-slate-400 uppercase tracking-wider" data-id="element-427" data-mp="18xldtl6mo9o1">
                                    {step.channel}
                                  </span>
                                </div>
                                <p className="text-sm text-slate-700 font-medium mb-2" data-id="element-428" data-mp="gfzizhh7xnp9">
                                  {step.subject}
                                </p>
                                {step.body && <p className="text-sm text-slate-500 whitespace-pre-wrap" data-id="element-429" data-mp="35ovxmppr5xlx">
                                    {step.body}
                                  </p>}
                              </div>
                            </div>)}
                        </div> : <p className="text-sm text-slate-500" data-id="element-430" data-mp="3olf9hmrxxzm6">
                          No steps yet. Add messages to this sequence to start
                          nurturing leads.
                        </p>}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>)}
        </div>
      </div>

      <CreateCampaignModal open={createOpen} onClose={() => setCreateOpen(false)} onCreate={handleCreate} data-id="element-431" data-mp="246vf5fzwpkgh" />

      <EditCampaignModal campaign={editingCampaign} open={editingCampaign !== null} onClose={() => setEditingCampaign(null)} onSave={handleUpdate} data-id="element-432" data-mp="1z67fy71ixxb6" />
    </Layout>;
}